// Tenantra Inc. - Select Tenant for Report Page (Microsoft 365 Style - Compact)
import { useEffect, useState } from 'react';
import { 
  FileText, Search, ChevronDown, ChevronRight, Building2, 
  User, Calendar, Filter, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { navigate } from '@/services/navigation';
import { TableSkeleton } from '@/components/dashboard/TableKit';
import api, { apiHelpers } from '@/services/api';
import SubscriptionGate from '@/components/SubscriptionGate';

export default function SelectTenantForReportPage() {
  const [loading, setLoading] = useState(true);
  const [tenancies, setTenancies] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');
  
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setSubscriptionRequired(false);
      const [tenanciesRes, tenantsRes, propertiesRes] = await Promise.all([
        api.get('/tenancies'),
        api.get('/tenants'),
        api.get('/properties?limit=500')
      ]);
      
      // Only keep ACTIVE tenancies - ended ones are ineligible for reports
      const activeTenancies = (tenanciesRes.data.items || []).filter(t => t.status === 'active');
      setTenancies(activeTenancies);
      setTenants(tenantsRes.data.items || []);
      setProperties(propertiesRes.data.items || []);
    } catch (err) {
      // Handle 402 Payment Required - show subscription gate
      if (err.response?.status === 402) {
        setSubscriptionRequired(true);
        setSubscriptionMessage(err.response?.data?.detail?.message || 'An active subscription is required to submit reports.');
        return;
      }
      toast.error(apiHelpers.handleError(err));
    } finally {
      setLoading(false);
    }
  };

  // Get tenant details by ID
  const getTenant = (tenantId) => tenants.find(t => t.id === tenantId);
  
  // Get property details by ID
  const getProperty = (propertyId) => properties.find(p => p.id === propertyId);

  // Filter tenancies based on selected property and search
  const filteredTenancies = tenancies.filter(tenancy => {
    // Property filter
    if (selectedProperty !== 'all' && tenancy.property_id !== selectedProperty) {
      return false;
    }
    
    // Search filter
    if (searchTerm) {
      const tenant = getTenant(tenancy.tenant_id);
      const property = getProperty(tenancy.property_id);
      const search = searchTerm.toLowerCase();
      
      const matchesName = tenant?.full_name?.toLowerCase().includes(search);
      const matchesEmail = tenant?.email?.toLowerCase().includes(search);
      const matchesAddress = property?.address?.street?.toLowerCase().includes(search);
      
      if (!matchesName && !matchesEmail && !matchesAddress) return false;
    }
    
    return true;
  });

  // Get properties that have active tenancies (for filter dropdown)
  const propertiesWithActiveTenancies = properties.filter(p => 
    tenancies.some(t => t.property_id === p.id)
  );

  const handleSelectTenant = (tenancy) => {
    navigate(`/landlord/reports/${tenancy.id}/new`);
  };

  if (loading) {
    return <TableSkeleton rows={5} spans={[5, 3, 2, 2]} />;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-lg font-semibold text-slate-800">Submit a Report</h1>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-teal-500" />
          <span className="text-sm font-medium text-slate-700">Filter Tenants</span>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Property Filter */}
          <div className="flex-1">
            <label className="text-xs text-slate-500 mb-1 block">Property</label>
            <div className="relative">
              <select
                value={selectedProperty}
                onChange={(e) => setSelectedProperty(e.target.value)}
                className="w-full h-8 px-2.5 pr-8 text-sm bg-slate-50 border border-slate-200 rounded-md text-slate-800 focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 appearance-none"
              >
                <option value="all">All Properties ({tenancies.length} active)</option>
                {propertiesWithActiveTenancies.map(p => {
                  const count = tenancies.filter(t => t.property_id === p.id).length;
                  return (
                    <option key={p.id} value={p.id}>
                      {p.address.street}{p.address.unit_number ? ` #${p.address.unit_number}` : ''} ({count})
                    </option>
                  );
                })}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            </div>
          </div>
          
          {/* Search */}
          <div className="flex-1">
            <label className="text-xs text-slate-500 mb-1 block">Search</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-8 pl-8 text-sm bg-slate-50 border-slate-200"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tenants List */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
          <div className="grid grid-cols-12 gap-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
            <div className="col-span-4">Tenant</div>
            <div className="col-span-4">Property</div>
            <div className="col-span-2">Since</div>
            <div className="col-span-2 text-right">Action</div>
          </div>
        </div>

        {/* Body */}
        {filteredTenancies.length === 0 ? (
          <div className="text-center py-10">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-2">
              <User className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-sm text-slate-600 font-medium">No active tenancies found</p>
            <p className="text-xs text-slate-500 mt-1">
              {selectedProperty !== 'all' 
                ? 'Try selecting a different property or clear filters'
                : 'Add tenants to your properties first'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredTenancies.map(tenancy => {
              const tenant = getTenant(tenancy.tenant_id);
              const property = getProperty(tenancy.property_id);
              
              if (!tenant || !property) return null;
              
              return (
                <div 
                  key={tenancy.id}
                  className="grid grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-slate-50 transition-colors"
                >
                  {/* Tenant */}
                  <div className="col-span-4 flex items-center gap-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{tenant.full_name}</p>
                      <p className="text-xs text-slate-500 truncate">{tenant.email}</p>
                    </div>
                  </div>
                  
                  {/* Property */}
                  <div className="col-span-4 flex items-center gap-2">
                    <Building2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-slate-700 truncate">
                        {property.address.street}
                        {property.address.unit_number && ` #${property.address.unit_number}`}
                      </p>
                      <p className="text-xs text-slate-500">{property.address.city}</p>
                    </div>
                  </div>
                  
                  {/* Move-in Date */}
                  <div className="col-span-2 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-sm text-slate-600">
                      {tenancy.start_date 
                        ? new Date(tenancy.start_date).toLocaleDateString('en-CA', { month: 'short', year: 'numeric' })
                        : 'N/A'}
                    </span>
                  </div>
                  
                  {/* Action */}
                  <div className="col-span-2 text-right">
                    <Button
                      size="sm"
                      onClick={() => handleSelectTenant(tenancy)}
                      className="h-7 px-3 text-xs bg-teal-600 hover:bg-teal-700"
                    >
                      Select
                      <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <p className="text-xs text-slate-500">
        Select the tenancy you are reporting on. The report covers one period: rent payment, lease status and any issues.
      </p>
    </div>
  );
}
